import { z } from "zod";

export const incidentSchema = z.object({
  incidentId: z.number().optional(),
  incidentActive: z.boolean().default(true),
  incidentStreetAddress: z.string().min(2),
  incidentCity: z.string().optional().nullable(),
  incidentState: z.string().min(2),
  incidentZip: z.string().min(3),
  incidentCounty: z.string().min(2),
  incidentIsAddressValidated: z.boolean().default(false),
  incidentLatitude: z.string().optional().nullable(),
  incidentLongitude: z.string().optional().nullable(),
  incidentGpsNote: z.string().optional().nullable(),
  incidentOwnerName: z.string().min(2),
  incidentOwnerPhone: z.string().min(2),
  incidentOwnerEmail: z.email().optional().nullable(),
  incidentOwnerStreetAddress: z.string().min(2),
  incidentOwnerCity: z.string().optional().nullable(),
  incidentOwnerState: z.string().min(2),
  incidentOwnerZip: z.string().min(3),
  incidentOwnerCounty: z.string().min(2),
  incidentIsOwnerAddressValidated: z.boolean().default(false),
  incidentOwnerLatitude: z.string().optional().nullable(),
  incidentOwnerLongitude: z.string().optional().nullable(),
  incidentOwnerGpsNote: z.string().optional().nullable(),
  incidentComment: z.string().optional().nullable(),
});

export const caseSchema = z.object({
  caseId: z.number().optional(),
  caseEpiStatus: z.string().min(2),
  caseDateOnset: z.string().optional().nullable(),
  caseDateReported: z.string().optional().nullable(),
  caseDateDied: z.string().optional().nullable(),
  caseIsNotified: z.boolean().default(false),
  caseSpecies: z.string().min(2),
  caseBreed: z.string().optional().nullable(),
  caseAgeInYears: z.number().optional(),
  caseVaccinationStatus: z.string().min(2),
  caseAnimalStatus: z.string().min(2),
  caseNumber: z.string().optional().nullable(),
  caseLabNumber: z.string().optional().nullable(),
  caseNote: z.string().optional().nullable(),
  caseActive: z.boolean().default(true),
});

export const emptyCaseForm: z.infer<typeof caseSchema> = {
  caseEpiStatus: "",
  caseDateOnset: "",
  caseDateReported: "",
  caseDateDied: "",
  caseIsNotified: false,
  caseSpecies: "",
  caseBreed: "",
  caseAgeInYears: undefined,
  caseVaccinationStatus: "",
  caseAnimalStatus: "",
  caseNumber: "",
  caseLabNumber: "",
  caseNote: "",
  caseActive: true,
};

export type IncidentFormInput = z.infer<typeof incidentSchema>;

export interface IncidentRecord extends IncidentFormInput {
  incidentId: number;
  createdUtc: string;
  updatedUtc: string;
}

export const emptyIncidentForm: IncidentFormInput = {
  incidentActive: true,
  incidentStreetAddress: "",
  incidentCity: "",
  incidentState: "",
  incidentZip: "",
  incidentCounty: "",
  incidentIsAddressValidated: false,
  incidentLatitude: "",
  incidentLongitude: "",
  incidentGpsNote: "",
  incidentOwnerName: "",
  incidentOwnerPhone: "",
  incidentOwnerEmail: "",
  incidentOwnerStreetAddress: "",
  incidentOwnerCity: "",
  incidentOwnerState: "",
  incidentOwnerZip: "",
  incidentOwnerCounty: "",
  incidentIsOwnerAddressValidated: false,
  incidentOwnerLatitude: "",
  incidentOwnerLongitude: "",
  incidentOwnerGpsNote: "",
  incidentComment: "",
};

// Interface for veterinary index page display - shows individual cases
export interface CaseIndexRecord {
  caseId: number; // Case ID (display this)
  incidentId: number; // Incident ID (link to this)
  incidentStreetAddress: string | null;
  incidentCity: string;
  incidentCountyName: string;
  caseDateOnset: string;
  caseDateReported: string;
}

// Combined schema using composition
export const incidentWithCasesSchema = incidentSchema.extend({
  cases: z.array(caseSchema).min(1, "At least one case is required"),
});

export type IncidentWithCasesFormInput = z.infer<
  typeof incidentWithCasesSchema
>;

export type CaseFormInput = z.infer<typeof caseSchema>;
